import angular from "angular";

import { CostAccountingDimensionDefinition, CostAccountingDimension, CostAccountingDimensionScope, UserDefinedRow } from "@salaxy/core";
import { UiHelpers } from "../../services";

/**
 * Controller for handling row dimension input in the CalcRowsEditor.
 */
export class CalcRowDimensionInputController implements angular.IController {
  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["UiHelpers"];

  /** Currently edited row */
  public row: UserDefinedRow;
  /** Current dimension defition */
  public definition: CostAccountingDimensionDefinition;
  /** Possible upper level (e.g. calculation) dimensions that are inherited */
  private _inherited: CostAccountingDimension[];
  /** Bindable read-only */
  public readOnly?: boolean;
  /**
   * Style of inputs.
   * If null or empty, displays normal input.
   * - "openeClose" shows readOnly-like input with toggle button to open the field for editing.
   */
  public inputMode: "" | "openClose";

  private _options: Array<{
    /** Option value. */
    value: any,
    /** Option text.  */
    text: string,
    /** Option description. */
    title?: string,
  }>= [];

  private _newDimensions: CostAccountingDimension[] = [];

  constructor(
    private uiHelpers: UiHelpers
  ) {
    // Dependency injection
  }

  /** Copy of inherited dimensions */
  public get inherited(): CostAccountingDimension[] {
    return this._inherited;
  }

  /** Copy of inherited dimensions */
  public set inherited(value: CostAccountingDimension[])  {
    this._inherited = angular.copy(value);
  }

  /**
   * Returns true if the dimension values are inherited.
   */
  public isInherited(): boolean {
    if(!this.row.accounting.dimensions.some((x) => x.id === this.definition.id && !!x.value)) {
      return !!this.cleanDimensions(this.inherited)?.some((x) => x.id === this.definition.id);
    }
    return false;
  }

  /**
   * Determines if cost sharing is enabled based on the current definition.
   * @returns {boolean} True if cost sharing is allowed, otherwise false.
   */
  public isCostSharingEnabled(): boolean {
    const dimensionIds = this.row.accounting.dimensions.map(d => d.id);
    if(dimensionIds.length !== new Set(dimensionIds).size) {
      return true;
    }
    return this.definition.allowCostSharing;
  }

  /**
   * Retrieves the cost sharing values for the current row dimension.
   * This method uses a caching mechanism to store the computed values for performance optimization. It truncates the text values to a maximum of 10 characters, appending "..." if the text exceeds this length
   * If the dimension is inherited, it filters and maps the inherited dimensions to the required format. Otherwise, it uses the current row's accounting dimensions.
   */
  public getCostSharingValues(): {text: string, value: number}[] {
    return this.uiHelpers.cache(this, "costSharingValues", () => {
      const truncate = (text: string | undefined): string => {
        if (!text) {
          return "";
        }
        if (text?.length <= 10) {
          return text;
        }
        return text?.substring(0, 10) + "...";
      };
      if(this.isInherited()) {
        return this.cleanDimensions(this.inherited).filter((x) => x.id === this.definition.id).map((x) => ({ text: truncate(this.definition.options.find(option => option.value === x.value).value), value: x.percent ?? 0 }));
      } else {
        return this.row.accounting.dimensions.filter((x) => x.id === this.definition.id).map((x) => ({ text: truncate(this.definition.options.find(option => option.value === x.value).value), value: x.percent ?? 0 }));
      }
    });
  }

  /**
   * Returns inherited single value.
   */
  public getInheritedSingleValue(): CostAccountingDimension | undefined {
    return (this.cleanDimensions(this.inherited) ?? []).find((x) => x.id === this.definition.id);
  }

  /**
   * Sets single value.
   */
  public setSingleValue(value: any) {
    const singleValue = this.row.accounting.dimensions?.find((x) => x.id === this.definition.id);
    if (singleValue) {
      singleValue.value = value;
    } else {
      this.row.accounting.dimensions.push({ id: this.definition.id, value });
    }
  }

  /**
   * Returns single value.
   */
  public getSingleValue() {
    let singleValue = this.row.accounting.dimensions?.find((x) => x.id === this.definition.id) ?? this._newDimensions.find((x) => x.id === this.definition.id);
    if (!singleValue && !this.readOnly) {
      singleValue = { id: this.definition.id };
      this._newDimensions.push(singleValue);
    }

    return singleValue;
  }

  /**
   * Returns true if options exists for the dimension.
   * @returns True, if an option exists for the value.
   */
  public hasOptions(): boolean {
    const opts = this.getOptions();
    return opts.filter(x => x.value).length > 0;
  }

  /**
   * Returns available options for dimension.
   */
  public getOptions() {
    if (!this._options) {
      this._options = [];
    }

    let newOptions = [];

    if (this.definition) {
      newOptions = this.definition.options.filter((x) =>
        x.scope !== CostAccountingDimensionScope.Hidden &&
        (
          // show if scope definition for dimension not given
          ( !x.scope || x.scope === CostAccountingDimensionScope.None) ||
          // or this scope
          (CostAccountingDimensionScope.Row === x.scope)
        )
      )
      .map( (x) => ({ value: x.value, text: x.text }));
    }

    this.modifyOptions(newOptions, this._options);
    return this._options;
  }

  private cleanDimensions(dimensions: CostAccountingDimension[]) {
    if (!dimensions) {
      return dimensions;
    }
    const cleaned = dimensions.filter(x => x?.value && x?.id);
    return cleaned;
  }

  private modifyOptions(
    source: any[],
    target: any[]) {
    if (angular.equals(source, target)) {
      return;
    }
    target.splice(0, target.length);
    target.push(...source);
  }
}